import { AgGridServerFilterMap, BasicTableOptions, ExternalTableFilter } from '@tassat/common-definitions';
import { FirmRecord } from '@tassat/fred-definitions';

export interface CollateralHistoryTableOptions
    extends Omit<BasicTableOptions, 'SelectedFilter'> {
    SelectedFilter: 'approval' | 'clientApproval' | 'rejected' | 'failed' | 'pending' | 'none' | 'collateral';
    SelectedFirm: string;
    SelectedFirmName: string;
    FirmCondition: string;
    SelectedAccount?: string;
    AccountCondition?: string;
    SelectedCurrency?: number;
    CurrencyCondition?: string;
}

type HistoryTableFilter = { column: string; operation: AgGridServerFilterMap; value: string; dbColumn?: string };

// Keeping for backwards compatibility
const clearSpecialCollateralColumns = (
    mappedColumn: string,
    currentFilters: ExternalTableFilter[]
): ExternalTableFilter[] => {
    if (mappedColumn === 'pledgeeAccount' || mappedColumn === 'pledgorAccount') {
        // handle client counterparty field which can be split in two directions
        const senderFilter = currentFilters.findIndex((filter) => filter.column === 'pledgorAccount');
        if (senderFilter !== -1) {
            currentFilters.splice(senderFilter, 1);
        }

        const recipientFilter = currentFilters.findIndex((filter) => filter.column === 'pledgeeAccount');
        if (recipientFilter !== -1) {
            currentFilters.splice(recipientFilter, 1);
        }
    }

    return currentFilters;
};

const adminClearSpecialCollateralColumns = (
    mappedColumn: string,
    currentFilters: ExternalTableFilter[]
): ExternalTableFilter[] => {
    // Clear adjustment flag if necessary when clearing transaction type filter
    if (mappedColumn === 'tranType') {
        const adjustmentFilter = currentFilters.findIndex((filter) => filter.column === 'adjustment');
        if (adjustmentFilter !== -1) {
            currentFilters.splice(adjustmentFilter, 1);
        }
    }

    return currentFilters;
};

// Keeping for backwards compatibility
export const collateralHistoryTableOptionsInitialState: CollateralHistoryTableOptions = {
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'AuditTime|DESC',
    DefaultSort: 'AuditTime|DESC',
    SelectedDateFilter: '',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: 'none',
    SelectedFirmName: '',
    FirmCondition: '',
    SelectedAccount: 'none',
    AccountCondition: '',
    TableFilters: [],
    FilterCondition: '',
    DefaultState: true,
    SelectedCurrency: 0,
    CurrencyCondition: '',
    clearSpecialColumns: clearSpecialCollateralColumns
};

export const adminCollateralHistoryTableOptionsInitialState: CollateralHistoryTableOptions = {
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'AuditTime|DESC',
    DefaultSort: 'AuditTime|DESC',
    SelectedDateFilter: '',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: 'none',
    SelectedFirmName: '',
    FirmCondition: '',
    SelectedAccount: 'none',
    AccountCondition: '',
    TableFilters: [],
    FilterCondition: '',
    SelectedCurrency: 0,
    CurrencyCondition: '',
    DefaultState: true,
    clearSpecialColumns: adminClearSpecialCollateralColumns
};

// Keeping for backwards compatibility
export const setCollateralTableOptionsInitialState = (firm: FirmRecord, accountId?: string): CollateralHistoryTableOptions => ({
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'AuditTime|DESC',
    DefaultSort: 'AuditTime|DESC',
    SelectedDateFilter: '',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: firm.FirmId,
    SelectedFirmName: firm.Name,
    FirmCondition: `PledgingFirmId|EQ|${firm.FirmId}`,
    SelectedAccount: accountId || '',
    AccountCondition: accountId ? `PledgingAccountAddress|EQ|${accountId}` : '',
    TableFilters: [],
    FilterCondition: '',
    DefaultState: true,
    clearSpecialColumns: clearSpecialCollateralColumns
});

export const setAdminCollateralTableOptionsInitialState = (
    firm: FirmRecord,
    accountId?: string
): CollateralHistoryTableOptions => ({
    PageNo: 1,
    RecordPerPage: 15,
    Sort: 'AuditTime|DESC',
    DefaultSort: 'AuditTime|DESC',
    SelectedDateFilter: '',
    DateCondition: '',
    SelectedFilter: 'none',
    SelectedFirm: firm.FirmId,
    SelectedFirmName: firm.Name,
    FirmCondition: `PledgingFirmId|EQ|${firm.FirmId}`,
    SelectedAccount: accountId || '',
    AccountCondition: accountId ? `PledgingAccountAddress|EQ|${accountId}` : '',
    TableFilters: [],
    FilterCondition: '',
    SelectedCurrency: 0,
    CurrencyCondition: '',
    DefaultState: true,
    clearSpecialColumns: adminClearSpecialCollateralColumns
});